import { useMutation } from '@apollo/client';
import React from 'react';
import useAllTopics from './useAllTopics';
import { useAppDispatch } from '../app/redux/useAppSelector';
import ConfirmationModal from '../components/ConfirmationModal';
import { gql } from '../graphql/generated/gql';
import { UIActions } from '../ui/uiSlice';
import useNavigate from '../utils/router/useNavigate';
import topicPath from '../utils/url/topicPath';

const DELETE_TOPIC_MUTATION = gql(/** @complexity 2 */ `
  mutation deleteTopic($id: ID!) {
    deleteTopic(id: $id) {
      id
    }
  }
`);

const useDeleteTopic = (id: string) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const allTopics = useAllTopics({ flatten: true });
  const [mutate] = useMutation(DELETE_TOPIC_MUTATION, { variables: { id } });

  return () => {
    const topic = allTopics.find((t) => t.id === id);
    if (!topic) return Promise.resolve();

    const {
      ancestors: [parent],
      childTopics,
      name,
      privacy,
    } = topic;

    return new Promise((resolve, reject) => {
      dispatch(
        UIActions.openModal(ConfirmationModal, {
          title: 'Delete this topic?',
          message: (
            <>
              <p>Deleted topics are gone forever.</p>
              {childTopics.length > 0 && (
                <p>
                  <strong>{name}</strong>&apos;s subtopics will{' '}
                  {parent ? (
                    <>
                      be moved to <strong>{parent.name}</strong>.
                    </>
                  ) : (
                    'become top level topics.'
                  )}
                </p>
              )}
              {privacy === 'PRIVATE' && (
                <p>
                  Posts that are uncategorized after the deletion will be
                  accessible by anyone.
                </p>
              )}
            </>
          ),
          confirmButtonText: 'Delete',
          onConfirm: async () => {
            const promise = mutate({
              variables: { id },
              optimisticResponse: {
                __typename: 'RootMutationType',
                deleteTopic: {
                  __typename: 'Topic',
                  id,
                },
              },
            });

            navigate(parent ? topicPath(parent) : '/');

            await promise.then(resolve).catch(reject);
          },
        }),
      );
    });
  };
};

export default useDeleteTopic;
